import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { customElement } from "../marcel/customElement";
import MrclText from '../marcel/MrclText';
import { useTranslation } from 'react-i18next';

interface FilterOption {
  id: string;
  width: number;
}

interface FilterGroup {
  id: string;
  options: FilterOption[];
}

export interface SelectedFilters {
  scope: string;
  type: string;
  channel: string;
  origin: string;
  [key: string]: string;
}

export const DEFAULT_CA_LIVE_FILTERS: SelectedFilters = {
  scope: 'france',
  type: 'all_types',
  channel: 'all_channels',
  origin: 'all_origins',
};

const FILTER_GROUPS: FilterGroup[] = [
  {
    id: 'scope',
    options: [
      { id: 'hyper', width: 75 },
      { id: 'market', width: 75 },
      { id: 'france', width: 100 },
    ],
  },
  {
    id: 'type',
    options: [
      { id: 'integre', width: 82 },
      { id: 'franchise', width: 90 },
      { id: 'all_types', width: 90 },
    ],
  },
  {
    id: 'origin',
    options: [
      { id: 'new_carrefour', width: 126 },
      { id: 'natif', width: 85 },
      { id: 'match', width: 65 },
      { id: 'all_origins', width: 60 },
    ],
  },
];

const FILTER_GROUP_LABEL_KEYS: Record<string, string> = {
  scope: 'ca_live.filters.groups.scope',
  type: 'ca_live.filters.groups.type',
  channel: 'ca_live.filters.groups.channel',
  origin: 'ca_live.filters.groups.origin',
};

const FILTER_OPTION_LABEL_KEYS: Record<string, string> = {
  france: 'ca_live.filters.options.france',
  market: 'ca_live.filters.options.market',
  hyper: 'ca_live.filters.options.hyper',
  integre: 'ca_live.filters.options.integre',
  franchise: 'ca_live.filters.options.franchise',
  all_types: 'ca_live.filters.options.all_types',
  magasin: 'ca_live.filters.options.magasin',
  drive: 'ca_live.filters.options.drive',
  all_channels: 'ca_live.filters.options.all_channels',
  new_carrefour: 'ca_live.filters.options.new_carrefour',
  natif: 'ca_live.filters.options.natif',
  match: 'ca_live.filters.options.match',
  all_origins: 'ca_live.filters.options.all_origins',
};

const FILTER_SUMMARY_LABEL_KEYS: Record<string, Record<string, string>> = {
  scope: {
    france: 'ca_live.filters.summary.france',
    market: 'ca_live.filters.summary.market',
    hyper: 'ca_live.filters.summary.hyper',
  },
  type: {
    integre: 'ca_live.filters.summary.integre',
    franchise: 'ca_live.filters.summary.franchise',
    all_types: 'ca_live.filters.summary.all_types',
  },
  channel: {
    magasin: 'ca_live.filters.summary.magasin',
    drive: 'ca_live.filters.summary.drive',
    all_channels: 'ca_live.filters.summary.all_channels',
  },
  origin: {
    new_carrefour: 'ca_live.filters.summary.new_carrefour',
    natif: 'ca_live.filters.summary.natif',
    match: 'ca_live.filters.summary.match',
    all_origins: 'ca_live.filters.summary.all_origins',
  },
};

type Translate = (key: string) => string;

interface CALiveFilterBarProps {
  value?: SelectedFilters;
  onFilterChange?: (filters: SelectedFilters) => void;
  variant?: 'inline' | 'panel';
}

export const getCALiveFilterLabel = (groupId: string, optionId: string, t: Translate) =>
  t(FILTER_SUMMARY_LABEL_KEYS[groupId]?.[optionId] ?? optionId);

export const getCALiveFiltersSummary = (filters: SelectedFilters, t: Translate) =>
  FILTER_GROUPS.map((group) => getCALiveFilterLabel(group.id, filters[group.id], t)).join(' + ');

const NO_FILTER_VALUES = new Set(['france', 'all_types', 'all_channels', 'all_origins']);

export const getCALiveApiParams = (filters: SelectedFilters): string => {
  const params = new URLSearchParams();

  if (filters.scope && !NO_FILTER_VALUES.has(filters.scope)) {
    params.set('storeType', filters.scope.toUpperCase());
  }
  if (filters.type && !NO_FILTER_VALUES.has(filters.type)) {
    params.set('affiliation', filters.type.toUpperCase());
  }
  if (filters.origin && !NO_FILTER_VALUES.has(filters.origin)) {
    params.set('territoryType', filters.origin.toUpperCase());
  }

  return params.toString();
};

export default function CALiveFilterBar({ value, onFilterChange, variant = 'inline' }: CALiveFilterBarProps) {
  const { t } = useTranslation();
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilters>(value ?? DEFAULT_CA_LIVE_FILTERS);

  const selectedFiltersRef = useRef(selectedFilters);
  selectedFiltersRef.current = selectedFilters;

  useEffect(() => {
    if (value) {
      selectedFiltersRef.current = value;
      setSelectedFilters(value);
    }
  }, [value]);

  const handleSelect = (groupId: string, optionId: string) => {
    selectedFiltersRef.current = { ...selectedFiltersRef.current, [groupId]: optionId };
    setSelectedFilters(selectedFiltersRef.current);
    if (onFilterChange) {
      onFilterChange(selectedFiltersRef.current);
    }
  };

  return (
    <FilterContainer $variant={variant}>
      <FilterWrapper $variant={variant}>
        {variant === 'inline' && (
          <FilterLabel size="m">
            {t('ca_live.filters.label')}
          </FilterLabel>
        )}

        <FilterGroupsContainer $variant={variant}>
          {FILTER_GROUPS.map((group) => (
            <FilterGroupBlock key={group.id} $variant={variant}>
              {variant === 'panel' && (
                <GroupLabel>{t(FILTER_GROUP_LABEL_KEYS[group.id])}</GroupLabel>
              )}
              <ButtonGroup
                role="group"
                $groupId={group.id}
                $variant={variant}
              >
                {group.options.map((option) => {
                  const isActive = selectedFilters[group.id] === option.id;
                  return (
                    <FilterButton
                      key={option.id}
                      type="button"
                      aria-pressed={isActive}
                      onClick={() => handleSelect(group.id, option.id)}
                      variant={isActive ? 'filled' : 'transparent'}
                      tone={isActive ? 'brand' : 'neutral'}
                      size="s"
                      style={{ width: option.width }}
                    >
                      {t(FILTER_OPTION_LABEL_KEYS[option.id])}
                    </FilterButton>
                  );
                })}
              </ButtonGroup>
            </FilterGroupBlock>
          ))}
        </FilterGroupsContainer>
      </FilterWrapper>
    </FilterContainer>
  );
}

const FilterContainer = styled.div<{ $variant: 'inline' | 'panel' }>`
  width: ${({ $variant }) => ($variant === 'panel' ? '100%' : 'calc(100% - 24px)')};
  max-width: 1580px;
  height: ${({ $variant }) => ($variant === 'panel' ? '390px' : 'auto')};
  min-height: ${({ $variant }) => ($variant === 'panel' ? 'auto' : '66px')};
  margin: ${({ $variant }) => ($variant === 'panel' ? '0' : '12px auto 0')};
  box-sizing: border-box;
  background-color: #ffffff;
  border-radius: ${({ $variant }) => ($variant === 'panel' ? '8px' : '10px')};
  border: 1px solid #e5e7eb;
  padding: ${({ $variant }) => ($variant === 'panel' ? '16px' : '12px')};
  display: flex;
  align-items: center;
  box-shadow: ${({ $variant }) => ($variant === 'panel' ? '0px 1px 3px rgba(0, 0, 0, 0.12)' : 'none')};

  @media (min-width: 1440px) {
    padding: 16px 12px;
  }
`;

const FilterWrapper = styled.div<{ $variant: 'inline' | 'panel' }>`
  display: flex;
  align-items: ${({ $variant }) => ($variant === 'panel' ? 'stretch' : 'center')};
  flex-wrap: wrap;
  gap: 12px 16px;
  width: 100%;
  height: ${({ $variant }) => ($variant === 'panel' ? '100%' : 'auto')};
  flex-direction: ${({ $variant }) => ($variant === 'panel' ? 'column' : 'row')};

  @media (min-width: 640px) {
    gap: 12px 24px;
  }
`;

const FilterLabel = styled(MrclText)`
  color: #454545;
  font-size: 14px !important;
  margin-right: 8px;
`;

const FilterGroupsContainer = styled.div<{ $variant: 'inline' | 'panel' }>`
  display: flex;
  align-items: ${({ $variant }) => ($variant === 'panel' ? 'stretch' : 'center')};
  flex-wrap: ${({ $variant }) => ($variant === 'panel' ? 'nowrap' : 'wrap')};
  gap: ${({ $variant }) => ($variant === 'panel' ? '0' : '12px 16px')};
  flex-direction: ${({ $variant }) => ($variant === 'panel' ? 'column' : 'row')};
  justify-content: ${({ $variant }) => ($variant === 'panel' ? 'space-between' : 'flex-start')};
  height: ${({ $variant }) => ($variant === 'panel' ? '100%' : 'auto')};

  @media (min-width: 640px) {
    gap: ${({ $variant }) => ($variant === 'panel' ? '0' : '12px 24px')};
  }
`;

const FilterGroupBlock = styled.div<{ $variant: 'inline' | 'panel' }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: ${({ $variant }) => ($variant === 'panel' ? 'flex-start' : 'auto')};
  width: ${({ $variant }) => ($variant === 'panel' ? 'fit-content' : 'auto')};
  gap: ${({ $variant }) => ($variant === 'panel' ? '8px' : '0')};
`;

const GroupLabel = styled.span`
  display: block;
  width: 100%;
  color: #454545;
  font-size: 12px;
  line-height: 16px;
  text-align: left;
`;

const ButtonGroup = styled.div<{ $groupId: string; $variant: 'inline' | 'panel' }>`
  display: inline-flex;
  align-items: center;
  background-color: #f7f7f7;
  height: 40px;
  padding: 4px;
  border-radius: 4px;
  gap: 0;
  flex-shrink: 0;
  box-sizing: border-box;
`;

// mrcl-button styles its inner button from Marcel tokens inside its Shadow DOM;
// custom properties are the only styles that cross it, so override them here.
const FilterButton = styled(customElement("mrcl-button"))`
  --mrcl-body-m-bold: 500 14px/20px var(--mrcl-font-family-default);
  --mrcl-sizing-3xl: 32px;
  --mrcl-spacing-s: 0;
  --mrcl-spacing-l: 0;
`;
